export interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = 'REQUEST_FAILED',
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ResponseParser<T> {
  parse(value: unknown): T;
}

declare global {
  interface Window {
    __LILIUS_API_BASE__?: string;
  }
}

const API_BASE =
  (typeof window !== 'undefined' && window.__LILIUS_API_BASE__) ||
  import.meta.env.VITE_API_BASE ||
  '/api';

export const apiUrl = (path: string) => `${API_BASE}${path}`;

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return response.text();
  return response.json();
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit & { idempotencyKey?: string; schema?: ResponseParser<T> } = {},
): Promise<T> {
  const { idempotencyKey, schema, ...requestInit } = init;
  const headers = new Headers(requestInit.headers);
  if (requestInit.body && !(requestInit.body instanceof FormData))
    headers.set('Content-Type', 'application/json');
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);

  const response = await fetch(apiUrl(path), {
    ...requestInit,
    headers,
    credentials: 'include',
  });
  const body = await parseBody(response);

  if (!response.ok) {
    const payload = (body ?? {}) as ApiErrorBody;
    throw new ApiError(
      payload.error?.message || `Yêu cầu thất bại (${response.status})`,
      response.status,
      payload.error?.code,
      payload.error?.requestId,
    );
  }

  return schema ? schema.parse(body) : (body as T);
}

export function createIdempotencyKey(prefix: string): string {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${id}`;
}

const pendingIdempotencyKeys = new Map<string, string>();

function readPendingKey(scope: string): string | null {
  try {
    return sessionStorage.getItem(`lilius.idempotency.${scope}`);
  } catch {
    return pendingIdempotencyKeys.get(scope) ?? null;
  }
}

function writePendingKey(scope: string, value: string | null): void {
  if (value === null) pendingIdempotencyKeys.delete(scope);
  else pendingIdempotencyKeys.set(scope, value);
  try {
    const key = `lilius.idempotency.${scope}`;
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // The in-memory map still protects retries when storage is unavailable.
  }
}

export async function withIdempotencyKey<T>(
  scope: string,
  prefix: string,
  request: (key: string) => Promise<T>,
): Promise<T> {
  const key = readPendingKey(scope) ?? createIdempotencyKey(prefix);
  writePendingKey(scope, key);
  const result = await request(key);
  writePendingKey(scope, null);
  return result;
}

export const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Đã có lỗi xảy ra. Vui lòng thử lại.';
